// Iter 306 — one inquiry's thread: messages, reply box, staff resolve.
import { useState } from "react";
import { CheckCircle2, Flag, HelpCircle, Loader2, RotateCcw, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const when = (s) => (s || "").slice(0, 16).replace("T", " ");

export function InquiryStatusPill({ status, kind }) {
  const cls = status === "resolved" ? "bg-emerald-100 text-emerald-700 ring-emerald-200"
    : status === "answered" ? "bg-sky-100 text-sky-700 ring-sky-200"
    : kind === "review" ? "bg-amber-100 text-amber-800 ring-amber-200" : "bg-slate-100 text-slate-600 ring-slate-200";
  const label = status === "resolved" ? "Resolved" : status === "answered" ? "Answered" : kind === "review" ? "Review requested" : "Question open";
  return <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset ${cls}`}>{label}</span>;
}

export function InquiryThread({ inquiry, isStaff, onChange, onReissue }) {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const [resolving, setResolving] = useState(false);
  const post = async (path, body, okMsg) => {
    setBusy(path);
    try {
      const { data } = await api.post(`/commissions/inquiries/${inquiry.inquiry_id}/${path}`, body);
      toast.success(okMsg); onChange?.(data); setReply(""); setNote(""); setResolving(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); } finally { setBusy(""); }
  };
  const open = inquiry.status !== "resolved";
  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid={`inquiry-${inquiry.inquiry_id}`}>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-3 py-2 text-xs">
        {inquiry.kind === "review" ? <Flag className="h-3.5 w-3.5 text-amber-600" /> : <HelpCircle className="h-3.5 w-3.5 text-primary" />}
        <b className="text-slate-800">{inquiry.period_label}</b>
        {isStaff && <span className="text-slate-500">· {inquiry.partner_name}</span>}
        <InquiryStatusPill status={inquiry.status} kind={inquiry.kind} />
        {inquiry.domains?.length > 0 && <span className="text-slate-500">· {inquiry.domains.join(", ")}</span>}
        <span className="ml-auto text-slate-400">opened {when(inquiry.created_at)} by {inquiry.created_by_name}</span>
      </div>
      <ul className="max-h-72 space-y-2 overflow-y-auto px-3 py-2">
        {(inquiry.messages || []).map((m, i) => (
          <li key={i} className={`rounded-md px-2.5 py-1.5 text-xs ${m.system ? "border border-dashed border-slate-200 text-slate-500 italic"
            : m.side === "staff" ? "bg-slate-100 text-slate-800" : "bg-sky-50 text-slate-800"}`} data-testid={`inquiry-msg-${i}`}>
            <div className="mb-0.5 text-[10px] text-slate-400">{m.name} · {m.side === "staff" ? "team" : "partner"} · {when(m.at)}</div>
            <div className="whitespace-pre-wrap">{m.body}</div>
          </li>
        ))}
      </ul>
      <div className="space-y-2 border-t border-slate-100 px-3 py-2">
        {open && (
          <div className="flex gap-2">
            <Textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…" className="text-xs" data-testid="inquiry-reply-input" />
            <Button size="sm" variant="outline" disabled={!reply.trim() || busy !== ""} onClick={() => post("reply", { body: reply }, "Reply sent")} data-testid="inquiry-reply-send">
              {busy === "reply" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            </Button>
          </div>
        )}
        {isStaff && open && !resolving && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-700" onClick={() => setResolving(true)} data-testid="inquiry-resolve-open">
              <CheckCircle2 className="h-3.5 w-3.5" /> Resolve
            </Button>
            {onReissue && (
              <Button size="sm" variant="outline" className="h-7 text-xs text-violet-700" onClick={() => onReissue(inquiry)} data-testid="inquiry-reissue">
                <RotateCcw className="h-3.5 w-3.5" /> Re-issue corrected pro forma
              </Button>
            )}
          </div>
        )}
        {isStaff && open && resolving && (
          <div className="space-y-2 rounded-md bg-emerald-50 p-2">
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Resolution note the partner will see (optional)" className="text-xs" data-testid="inquiry-resolve-note" />
            <div className="flex flex-wrap gap-2">
              <Button size="sm" className="h-7 text-xs" disabled={busy !== ""} onClick={() => post("resolve", { note, outcome: "no_change" }, "Resolved — no change")} data-testid="inquiry-resolve-nochange">No change needed</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs text-violet-700" disabled={busy !== ""} onClick={() => post("resolve", { note, outcome: "corrected" }, "Resolved — correction to follow")} data-testid="inquiry-resolve-corrected">Resolved, correction to follow</Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setResolving(false)}>Cancel</Button>
            </div>
          </div>
        )}
        {isStaff && !open && (
          <div className="flex items-center justify-between text-[11px] text-slate-500">
            <span>Resolved {when(inquiry.resolved_at)} by {inquiry.resolved_by_name}{inquiry.outcome === "corrected" ? " — correction issued" : ""}</span>
            <div className="flex gap-2">
              {onReissue && inquiry.outcome === "corrected" && !inquiry.reissue_id && (
                <Button size="sm" variant="outline" className="h-6 text-[11px] text-violet-700" onClick={() => onReissue(inquiry)} data-testid="inquiry-reissue-after">
                  <RotateCcw className="h-3 w-3" /> Re-issue now
                </Button>
              )}
              <Button size="sm" variant="ghost" className="h-6 text-[11px]" disabled={busy !== ""} onClick={() => post("reopen", {}, "Reopened")} data-testid="inquiry-reopen">Reopen</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
